import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import type { ElementRef, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { Subject, debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { LabelsPipe } from '../../shared/pipes/labels/labels.pipe';
import { SafeHTMlPipe } from '../../shared/pipes/safe-html/safe-html.pipe';
import { PalantirAvatarComponent } from '../avatar/avatar.component';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirTagComponent } from '../tag/tag.component';
import { PalantirTextComponent } from '../text/text.component';
import type {
  PalantirSelectOption,
  PalantirSelectResult,
  PalantirSelectSearchParams,
} from './select.component.type';

@Component({
  selector: 'palantir-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    OverlayModule,
    PalantirTextComponent,
    PalantirIconComponent,
    PalantirAvatarComponent,
    InfiniteScrollDirective,
    PalantirTagComponent,
    SafeHTMlPipe,
    LabelsPipe,
  ],
})
export class PalantirSelectComponent implements OnInit, OnChanges {
  @ViewChild('input') private input: ElementRef<HTMLInputElement> | null = null;

  @ViewChild('options') private optionsContainer: ElementRef<HTMLDivElement> | null = null;

  @Input() public label?: string;

  @Input({ required: true }) public placeholder!: string;

  @Input({ required: true }) public emptyState!: string;

  @Input() public control?: AbstractControl<any, any> | null;

  @Input({ required: true }) public mapper!: (option: any) => PalantirSelectOption;

  @Input() public options: any[] = [];

  @Input() public disableInfiniteScroll: boolean = false;

  @Input() public search: (
    params: PalantirSelectSearchParams,
  ) => Promise<PalantirSelectResult<any>> = this.defaultSearch.bind(this);

  @Input() public valueKey?: string;

  @Output() public selected = new EventEmitter<any>();

  public _filteredOptions = signal<PalantirSelectOption[]>([]);

  public _options = signal<PalantirSelectOption[]>([]);

  public _selected = signal<PalantirSelectOption | null>(null);

  public optionsIsOpen = signal<boolean>(false);

  public showSelectedItem = signal<boolean>(false);

  public isRequired = signal<boolean>(false);

  public isSearchLoading = signal<boolean>(false);

  public isLoadingMoreLoading = signal<boolean>(false);

  public infiniteScrollDisabled = signal<boolean>(false);

  public filter$ = new Subject<string | null>();

  public loadMore$ = new Subject<void>();

  private take = 10;

  private skip = 0;

  private currentSearchTerm: string | null = null;

  private originalOptions: any[] = [];

  public constructor(@Inject(DestroyRef) private readonly destroyRef: DestroyRef) {}

  public async ngOnInit(): Promise<void> {
    this.isRequired.set(this.getRequiredValidator(this.control));

    this.filter$
      ?.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500), distinctUntilChanged())
      .subscribe(async (value) => {
        this.take = 10;
        this.skip = 0;

        this.isSearchLoading.set(true);

        this.optionsContainer?.nativeElement?.scrollTo({
          behavior: 'smooth',
          left: 0,
          top: 0,
        });

        this.currentSearchTerm = value;

        const result = await this._search(value);
        this._filteredOptions.set(result.items.map(this.mapper));
        this.originalOptions = result.items;

        this.infiniteScrollDisabled.set(
          this.disableInfiniteScroll || this._filteredOptions().length >= result.totalCount,
        );

        this.isSearchLoading.set(false);
      });

    this.loadMore$
      ?.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          if (!this.infiniteScrollDisabled()) {
            this.isLoadingMoreLoading.set(true);
          }
        }),
        debounceTime(200),
      )
      .subscribe(async () => {
        this.skip += this.take;

        this.optionsContainer?.nativeElement?.scrollTo({
          behavior: 'instant',
          left: 0,
          top: this.optionsContainer?.nativeElement?.scrollHeight,
        });

        const result = await this._search(this.currentSearchTerm);
        this._filteredOptions.update((options) => options.concat(result.items.map(this.mapper)));
        this.originalOptions = [...this.originalOptions, ...result.items];

        this.infiniteScrollDisabled.set(
          this.disableInfiniteScroll || this._filteredOptions().length >= result.totalCount,
        );

        this.isLoadingMoreLoading.set(false);
      });

    this.control?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((value) => {
        if (!value) {
          this.showSelectedItem.set(false);
          this._selected.set(null);
          return;
        }

        const option = this.valueKey
          ? this.originalOptions.find((opt) => opt[this.valueKey!] === value)
          : value;
        const _option = this.mapper(option);
        this._selected.set(_option);
        this.showSelectedItem.set(true);
      });

    await this.initializeOptions();
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['options']) {
      this._options.set(changes['options'].currentValue?.map(this.mapper) ?? []);
      this._filteredOptions.set(this._options());
      this.originalOptions = changes['options'].currentValue ?? [];

      await this.initializeOptions();
    }
  }

  public select(_option: PalantirSelectOption): void {
    const option = this.originalOptions.find((option) => option.id === _option.id);
    if (!option) {
      return;
    }

    const value = this.valueKey ? option[this.valueKey] : option;

    this.control?.patchValue(value);
    this.selected.emit(option);
    this.control?.markAsDirty();

    if (this.input) {
      this.input.nativeElement.value = '';
    }

    this.currentSearchTerm = null;
    this.skip = 0;

    this.filter$.next(null);
  }

  public deselect(): void {
    this.control?.patchValue(null);
    this.selected.emit(null);
    this.control?.markAsDirty();

    this.currentSearchTerm = null;
    this.skip = 0;

    this.filter$.next(null);

    setTimeout(() => {
      this.input?.nativeElement.focus({ preventScroll: true });
    }, 150);
  }

  public setOptionsIsOpen(isOpen: boolean) {
    setTimeout(() => {
      this.optionsIsOpen.set(isOpen);
    }, 150);

    if (!isOpen) {
      this.control?.markAsTouched();
    }

    if (!isOpen && !this.showSelectedItem() && this._selected()) {
      this.setShowSelectedItem(true);
    }
  }

  public setShowSelectedItem(value: boolean) {
    if (this.control?.disabled) {
      return;
    }

    setTimeout(() => {
      this.showSelectedItem.set(value);
    }, 150);

    setTimeout(() => {
      if (!value) {
        this.input?.nativeElement.focus({ preventScroll: true });
      }
    }, 200);
  }

  public onScrollDown(): void {
    this.loadMore$.next();
  }

  private async _search(searchTerm: string | null): Promise<PalantirSelectResult<any>> {
    const result = await this.search({
      searchTerm,
      take: this.take,
      skip: this.skip,
    });

    return {
      items: result.items,
      totalCount: result.totalCount,
    };
  }

  private async defaultSearch(
    params: PalantirSelectSearchParams,
  ): Promise<PalantirSelectResult<any>> {
    let filteredOptions = params?.searchTerm
      ? this._options().filter((option) => {
          const loweredSearchTerm = params?.searchTerm?.toLowerCase() ?? '';

          return (
            option.title?.toLowerCase().includes(loweredSearchTerm) ??
            option.description.toLowerCase().includes(loweredSearchTerm)
          );
        })
      : this._options();

    filteredOptions = filteredOptions.map((option) =>
      this.options.find((opt) => opt.id === option.id),
    );

    return {
      items: filteredOptions,
      totalCount: filteredOptions.length,
    };
  }

  private getRequiredValidator(control?: AbstractControl<any, any> | null): boolean {
    const validatorFn = control?.validator;

    if (!validatorFn) {
      return false;
    }

    const errors = validatorFn(new FormControl({ value: '', disabled: false }));
    return errors?.['required'] ?? false;
  }

  private async initializeOptions(): Promise<void> {
    if (this.control?.value) {
      const option = this.valueKey
        ? this.originalOptions.find((opt) => opt[this.valueKey!] === this.control?.value)
        : this.control?.value;

      const selectedOption = this.mapper(option);
      this._selected.set(selectedOption);
      this.showSelectedItem.set(true);
    }

    const result = await this._search(null);

    this._options.set(result.items.map(this.mapper));
    this._filteredOptions.set(result.items.map(this.mapper));
    this.originalOptions = result.items;

    this.infiniteScrollDisabled.set(
      this.disableInfiniteScroll || this._filteredOptions().length >= result.totalCount,
    );
  }

  public trackByFn(index: number, item: PalantirSelectOption): number | string {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return this.valueKey ? String(item[this.valueKey as keyof typeof item]) : index;
  }
}
